/**
 * Entity & Verification Types
 * Shared types for the 4-entity onboarding + dual-mode verification
 */

// ─── Entity Types ────────────────────────────────────────────
export type EntityType = 'individual' | 'proprietorship' | 'partnership' | 'llp_pvt_ltd';

export const ENTITY_TYPE_LABELS: Record<EntityType, string> = {
  individual: 'Individual',
  proprietorship: 'Proprietorship',
  partnership: 'Partnership Firm',
  llp_pvt_ltd: 'LLP / Pvt. Ltd.',
};

export const ENTITY_TYPE_DESCRIPTIONS: Record<EntityType, string> = {
  individual: 'Single person operating as a DSA/Connector',
  proprietorship: 'Sole proprietorship with trade name',
  partnership: 'Partnership firm with 2+ partners',
  llp_pvt_ltd: 'LLP or Private Limited company',
};

// ─── Verification Types ──────────────────────────────────────
export type VerificationType = 'pan' | 'aadhaar' | 'gstin' | 'bank' | 'cin' | 'din';

export type VerificationMethod = 'api' | 'manual_ocr' | 'manual_entry';

export type VerificationStatusValue =
  | 'not_started'
  | 'api_pending'
  | 'api_ok'
  | 'api_failed'
  | 'manual_upload'
  | 'ocr_processing'
  | 'ocr_ok'
  | 'ocr_failed'
  | 'verified'
  | 'manual_review';

export interface VerificationStatus {
  status: VerificationStatusValue;
  method: VerificationMethod | null;
  confidence: number;
  data: Record<string, any>;
  documentUrl: string | null;
  requiresManualReview: boolean;
  errorMessage: string | null;
  timestamp: string | null;
}

export const INITIAL_VERIFICATION_STATUS: VerificationStatus = {
  status: 'not_started',
  method: null,
  confidence: 0,
  data: {},
  documentUrl: null,
  requiresManualReview: false,
  errorMessage: null,
  timestamp: null,
};

export interface VerificationResult {
  verified: boolean;
  method: VerificationMethod;
  confidence: number;
  data: Record<string, any>;
  documentUrl?: string;
  requiresManualReview: boolean;
}

// ─── Partner / Director ──────────────────────────────────────
export type PartnerDesignation = 'partner' | 'designated_partner' | 'director' | 'managing_director';

export interface Partner {
  id: string;
  fullName: string;
  designation: PartnerDesignation;
  dinDpin: string;
  stakePercentage: number;
  aadhaarNumber: string;
  panNumber: string;
  photoUrl: string;
  isAuthorizedSignatory: boolean;
  isUBO: boolean;
  verification: {
    aadhaar: VerificationStatus;
    pan: VerificationStatus;
    din: VerificationStatus;
  };
}

export const createEmptyPartner = (id: string): Partner => ({
  id,
  fullName: '',
  designation: 'partner',
  dinDpin: '',
  stakePercentage: 0,
  aadhaarNumber: '',
  panNumber: '',
  photoUrl: '',
  isAuthorizedSignatory: false,
  isUBO: false,
  verification: {
    aadhaar: { ...INITIAL_VERIFICATION_STATUS },
    pan: { ...INITIAL_VERIFICATION_STATUS },
    din: { ...INITIAL_VERIFICATION_STATUS },
  },
});

// ─── UBO ─────────────────────────────────────────────────────
export interface UBOEntry {
  name: string;
  identityProofType: string;
  identityProofNumber: string;
  identityProofUrl: string;
  stakePercentage: number;
  controlType: 'shareholding' | 'voting' | 'control' | 'other';
}

// ─── Business Details ────────────────────────────────────────
export interface BusinessDetails {
  tradeName: string;
  cin: string;
  llpin: string;
  udyamNumber: string;
  shopsEstNumber: string;
  dateOfIncorporation: string;
  registrationNumber: string;
  registeredAddress: {
    line1: string;
    line2: string;
    city: string;
    state: string;
    pinCode: string;
  };
  operatingAddress: {
    line1: string;
    line2: string;
    city: string;
    state: string;
    pinCode: string;
  };
}

// ─── Consent (DPDP) ─────────────────────────────────────────
export type ConsentCategory =
  | 'personal_data_collection'
  | 'aadhaar_ekyc'
  | 'pan_verification'
  | 'bank_verification'
  | 'gstin_verification'
  | 'data_sharing_with_lenders'
  | 'document_storage'
  | 'communication';

export interface ConsentEntry {
  category: ConsentCategory;
  granted: boolean;
  grantedAt: string | null;
  consentVersion: string;
  required: boolean;
  label: string;
  description: string;
}

export const CONSENT_CATEGORIES: ConsentEntry[] = [
  { category: 'personal_data_collection', granted: false, grantedAt: null, consentVersion: '1.0', required: true, label: 'Personal Data Collection', description: 'Collection of your name, date of birth, contact details, and identification numbers for onboarding purposes.' },
  { category: 'aadhaar_ekyc', granted: false, grantedAt: null, consentVersion: '1.0', required: true, label: 'Aadhaar eKYC', description: 'Use of your Aadhaar number for electronic Know Your Customer verification via UIDAI.' },
  { category: 'pan_verification', granted: false, grantedAt: null, consentVersion: '1.0', required: true, label: 'PAN Verification', description: 'Verification of your PAN with NSDL/CDSL for identity and tax compliance.' },
  { category: 'bank_verification', granted: false, grantedAt: null, consentVersion: '1.0', required: true, label: 'Bank Account Verification', description: 'Penny drop verification of your bank account for commission disbursement.' },
  { category: 'gstin_verification', granted: false, grantedAt: null, consentVersion: '1.0', required: false, label: 'GSTIN Verification', description: 'Verification of your GST registration with GSTN for tax compliance.' },
  { category: 'data_sharing_with_lenders', granted: false, grantedAt: null, consentVersion: '1.0', required: true, label: 'Data Sharing with Lenders', description: 'Sharing your onboarding data with lending partner institutions.' },
  { category: 'document_storage', granted: false, grantedAt: null, consentVersion: '1.0', required: true, label: 'Document Storage', description: 'Secure storage of your uploaded documents for a maximum of 7 years as per regulatory requirements.' },
  { category: 'communication', granted: false, grantedAt: null, consentVersion: '1.0', required: false, label: 'Communication', description: 'Receiving updates, notifications, and promotional content via email and SMS.' },
];

// ─── Step Config ─────────────────────────────────────────────
export type StepId =
  | 'entity-type'
  | 'aadhaar-kyc'
  | 'basic-details'
  | 'address'
  | 'pan-verification'
  | 'bank-verification'
  | 'gstin'
  | 'live-photo'
  | 'consent'
  | 'declarations'
  | 'mou-preview'
  | 'esign'
  | 'success';

export interface StepConfig {
  id: StepId;
  path: string;
  label: string;
  shortLabel: string;
  applicableTo: EntityType[];
  isRequired: boolean;
}
